import { useState, useRef } from 'react';
import { Upload, X, Loader2, Image } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}`;

export const ImageUpload = ({ value, onChange, className = '' }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API}/api/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      // Convert relative URL to full URL
      const fullUrl = `${API}${data.url}`;
      onChange(fullUrl);
      toast.success('Image uploaded!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const clearImage = () => {
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={className}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFileSelect}
        className="hidden"
        data-testid="file-input"
      />

      {value ? (
        <div className="relative">
          <div className="rounded-lg border-4 border-black overflow-hidden">
            <img 
              src={value} 
              alt="Uploaded" 
              className="w-full h-40 object-cover"
              onError={(e) => {
                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f0f0f0" width="100" height="100"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23999">Image Error</text></svg>';
              }}
            />
          </div>
          <button
            type="button"
            onClick={clearImage}
            className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full border-2 border-black hover:bg-red-600 transition-colors"
            data-testid="clear-image-btn"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <div
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`border-4 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
            ${dragOver 
              ? 'border-[#FF00FF] bg-[#FF00FF]/10' 
              : 'border-gray-300 hover:border-[#FF00FF] hover:bg-gray-50'
            }
            ${uploading ? 'pointer-events-none opacity-50' : ''}
          `}
          data-testid="upload-dropzone"
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-8 h-8 text-[#FF00FF] animate-spin" />
              <p className="font-space-grotesk text-gray-600">Uploading...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="bg-[#CCFF00] p-3 rounded-full border-2 border-black">
                <Upload className="w-6 h-6 text-black" />
              </div>
              <p className="font-space-grotesk font-semibold text-gray-700">
                Click to upload or drag & drop
              </p>
              <p className="font-space-grotesk text-sm text-gray-500">
                JPEG, PNG, GIF, WebP (max 5MB)
              </p>
            </div>
          )}
        </div>
      )}

      {/* URL input as fallback */}
      <div className="mt-3">
        <p className="font-space-grotesk text-xs text-gray-500 mb-1">Or paste image URL:</p>
        <input
          type="url"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://example.com/image.jpg"
          className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg font-space-grotesk text-sm focus:border-[#FF00FF] focus:outline-none"
          data-testid="image-url-input"
        />
      </div>
    </div>
  );
};

export default ImageUpload;
